'use client';

import { type JWTPayload, decodeJwt } from 'jose';

export interface WishlistJWTPayload extends JWTPayload {
  exp?: number;
  iat?: number;
}

/**
 * Decode a JWT token without verification (for reading expiry)
 * Note: This doesn't verify the signature, only decodes the payload
 */
export function decodeWishlistToken(token: string): WishlistJWTPayload | null {
  try {
    return decodeJwt(token) as WishlistJWTPayload;
  } catch (_error) {
    return null;
  }
}

/**
 * Check if a token is expired based on its JWT expiry claim
 * @param token - JWT token to check
 * @param bufferSeconds - Buffer time in seconds before actual expiry (default: 3600 = 1 hour)
 */
export function isTokenExpired(token: string, bufferSeconds = 3600): boolean {
  const payload = decodeWishlistToken(token);

  if (!payload || !payload.exp) {
    // If we can't decode or no expiry, consider it expired
    return true;
  }

  // Get current time in seconds
  const currentTime = Math.floor(Date.now() / 1000);

  // Check if token expires within the buffer time
  return payload.exp - bufferSeconds <= currentTime;
}

/**
 * Get the time until token expiry in seconds
 * @returns Seconds until expiry, or -1 if already expired or invalid
 */
export function getSecondsUntilExpiry(token: string): number {
  const payload = decodeWishlistToken(token);
  const expiry = payload?.exp;

  if (!expiry) {
    return -1;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const secondsUntilExpiry = expiry - currentTime;

  return secondsUntilExpiry > 0 ? secondsUntilExpiry : -1;
}
